import React from 'react';
import {
  AlertTriangle,
  TrendingDown,
  Unlink,
  Timer,
  Bug,
  CircleHelp,
  ShieldCheck,
  Zap,
  GitBranch,
  ArrowRight,
} from 'lucide-react';
import { useInView } from '../../hooks/useInView';
import { problemsData, solutionsData } from '../../data/mock';

const problemIconMap = {
  AlertTriangle,
  TrendingDown,
  Unlink,
  Timer,
  Bug,
  CircleHelp,
};

const solutionIconMap = {
  ShieldCheck,
  Zap,
  GitBranch,
};

const ProblemCard = ({ item, index }) => {
  const [ref, isInView] = useInView();
  const Icon = problemIconMap[item.iconName];

  return (
    <div
      ref={ref}
      className={`group p-6 rounded-xl border border-stone-800/60 bg-stone-900/50 hover:bg-stone-900/80 hover:border-stone-700/60 transition-[opacity,transform,background-color,border-color] duration-500 ease-out ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="w-10 h-10 rounded-lg bg-rose-500/10 flex items-center justify-center mb-4">
        {Icon && <Icon className="w-5 h-5 text-rose-400" />}
      </div>
      <h3 className="text-base font-semibold text-stone-100 mb-2">
        {item.title}
      </h3>
      <p className="text-sm text-stone-400 leading-relaxed">
        {item.description}
      </p>
    </div>
  );
};

const SolutionCard = ({ item, index }) => {
  const [ref, isInView] = useInView();
  const Icon = solutionIconMap[item.iconName];

  return (
    <div
      ref={ref}
      className={`group p-8 rounded-xl border border-amber-500/20 bg-amber-500/[0.06] hover:bg-amber-500/10 hover:border-amber-500/30 transition-[opacity,transform,background-color,border-color] duration-500 ease-out ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <div className="w-12 h-12 rounded-xl bg-amber-500/15 flex items-center justify-center mb-5">
        {Icon && <Icon className="w-6 h-6 text-amber-400" />}
      </div>
      <h3 className="text-lg font-semibold text-stone-100 mb-2">
        {item.title}
      </h3>
      <p className="text-sm text-stone-400 leading-relaxed">
        {item.description}
      </p>
    </div>
  );
};

export const ProblemSolutionSection = () => {
  const [titleRef, titleInView] = useInView();
  const [solTitleRef, solTitleInView] = useInView();

  return (
    <section className="relative bg-stone-950 py-24 lg:py-32 overflow-hidden">
      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Problems */}
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-[opacity,transform] duration-700 ease-out ${
            titleInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-rose-400 mb-3 block">
            The Problem
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-100 mb-4">
            Why Code Quality Matters
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto text-lg">
            These issues compound silently — slowing teams, increasing risk, and
            making every change harder than the last.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-24">
          {problemsData.map((item, i) => (
            <ProblemCard key={item.title} item={item} index={i} />
          ))}
        </div>

        {/* Divider */}
        <div className="flex items-center justify-center mb-24">
          <div className="h-px w-24 bg-stone-800" />
          <div className="mx-4">
            <ArrowRight className="w-5 h-5 text-amber-500" />
          </div>
          <div className="h-px w-24 bg-stone-800" />
        </div>

        {/* Solutions */}
        <div
          ref={solTitleRef}
          className={`text-center mb-16 transition-[opacity,transform] duration-700 ease-out ${
            solTitleInView
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-6'
          }`}
        >
          <span className="text-xs font-semibold tracking-widest uppercase text-amber-400 mb-3 block">
            The Solution
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-100 mb-4">
            Professional Code Reviews That Deliver
          </h2>
          <p className="text-stone-400 max-w-2xl mx-auto text-lg">
            A fresh, expert perspective on your codebase — turning risk into
            confidence and complexity into clarity.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {solutionsData.map((item, i) => (
            <SolutionCard key={item.title} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
